// Client-side credit pricing.
//
// The server is the authority — packages/functions/users/credits/
// calculations.py owns the real formula and every callable re-computes the
// charge before it takes the money. This module exists so the UI can quote a
// price BEFORE the click, and its only job is to agree with that formula:
//
//     credits = ceil(duration_minutes x resolution_multiplier)
//
// Keep RESOLUTION_MULTIPLIERS in lock-step with RESOLUTION_MULTIPLIERS in
// calculations.py. A disagreement here is worse than no estimate at all: the
// user approves one number and is charged another.

export const RESOLUTION_MULTIPLIERS = {
    '720p': 0.5,
    '1080p': 1.0,
    '4k': 3.5,
};

// The tiers a photo compile may be rendered at. No 720p: a compile is built
// from stills that are essentially never that small, so the tier would only
// ever sell a worse video for half price. Mirrors
// PHOTO_COMPILE_ALLOWED_RESOLUTIONS in functions/timelapse/api/main.py.
export const COMPILE_RESOLUTIONS = ['1080p', '4k'];
export const DEFAULT_COMPILE_RESOLUTION = '1080p';

// Human labels for the tiers, so the picker and any copy stay consistent.
export const RESOLUTION_LABELS = {
    '720p': '720p',
    '1080p': '1080p HD',
    '4k': '4K',
};

/**
 * Map a source video's pixel height onto a pricing tier.
 * Mirrors the height branch in generate_edited_video.
 */
export const resolutionFromHeight = (height) => {
    // `Number(null)` is 0 and `Number('')` is 0, both of which would sail
    // through the finite check and price an unknown source as the CHEAPEST
    // tier. Only a real number counts.
    const h = typeof height === 'number' ? height : Number.NaN;
    if (!Number.isFinite(h) || h <= 0) return '1080p';
    if (h >= 2160) return '4k';
    if (h <= 720) return '720p';
    return '1080p';
};

/**
 * ceil(minutes x multiplier), or null when the inputs can't produce a number.
 * Returns null rather than 0 so callers can tell "free" from "unknown" and
 * hide the estimate instead of quoting a wrong one.
 */
export const creditsForMinutes = (durationMinutes, resolution) => {
    const minutes = Number(durationMinutes);
    const multiplier = RESOLUTION_MULTIPLIERS[String(resolution).toLowerCase()];
    if (!Number.isFinite(minutes) || minutes < 0 || multiplier === undefined) return null;
    return Math.ceil(minutes * multiplier);
};

/**
 * What compiling `photoCount` photos at `fps` costs, before any credit for
 * compiles already paid for on this project.
 */
export const compileCreditCost = ({ photoCount, fps, resolution = DEFAULT_COMPILE_RESOLUTION }) => {
    const count = Number(photoCount);
    const rate = Number(fps);
    if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(rate) || rate <= 0) return null;
    return creditsForMinutes(count / rate / 60, resolution);
};

/**
 * What THIS compile will actually charge: the cost of the requested spec minus
 * what the project has already paid (`compileBilling.paidCredits`).
 *
 * Mirrors `max(0, cost - paidCredits)` in compile_photos, which is what makes
 * rebuilding at the same spec free and an upgrade cost only the difference.
 * Never negative — a downgrade is banked, not refunded.
 */
export const compileCreditCharge = ({
    photoCount,
    fps,
    resolution = DEFAULT_COMPILE_RESOLUTION,
    paidCredits = 0,
}) => {
    const cost = compileCreditCost({ photoCount, fps, resolution });
    if (cost === null) return null;
    const paid = Number(paidCredits);
    return Math.max(0, cost - (Number.isFinite(paid) && paid > 0 ? paid : 0));
};
